import BlankCellViewer from '@/components/dataGrid/components/renderers/cell/view/blankCellViewer.vue';
import AddColumnHeader from '@/components/dataGrid/components/renderers/headers/AddColumnHeader.vue';
import Helpers from '@/helpers/index.js';
import ProjectsHelpers, { PROJECT_DELIVERABLE_TRACKING_ENTITY_FIELDS } from '@/helpers/projects.helpers.ts';
import { API } from '@/services';
import {
  REMOVE_PROJECT_DELIVERABLE_MUTATION,
  SYNC_PROJECT_DELIVERABLE_PRODUCTS_MUTATION,
  UPDATE_PROJECT_DELIVERABLE_ASSET_STATUS_MUTATION,
  UPDATE_PROJECT_DELIVERABLE_MUTATION,
  UPDATE_PROJECT_DELIVERABLE_STATUS_MUTATION,
} from '@/services/project/mutations/deliverable/index.js';
import { CREATE_ENTITY } from '@/services/account/mutations/account/index.js';
import { UPDATE_CREATOR_CONTRACT_MUTATION } from '@/services/project/mutations/contract/index.js';
import { PROJECT_CREATOR_SYNC_BRIEFINGFILES_MUTATION, UPDATE_CREATOR_MUTATION, UPDATE_PROJECT_CREATOR_MUTATION } from '@/services/project/mutations/creator/index.js';
import { UPDATE_PROJECT_VIEWS_MUTATION } from '@/services/project/mutations/project/index.js';
import { GET_PROJECT_DELIVERABLES_QUERY, GET_PROJECT_ENTITY_MAPPING_QUERY } from '@/services/project/queries/index.js';
import { useProjectStore } from '@/stores/project/index.store.ts';
import { defineStore } from 'pinia';

const CREATOR_TRACKING_VIEW_NAME = 'Creator Tracking';
const CONTENT_SUBMITTED_VIEW_NAME = 'Content Submitted';

const normalizeCustomPropertyKey = (fieldName) => `${fieldName.replaceAll(' ', '').toLowerCase()}_custom_property`;
const normalizeCustomPropertyField = (fieldName) => `${normalizeCustomPropertyKey(fieldName)}.display_value`;

const CREATOR_TRACKING_CREATOR_FIELDS = [
  {
    field: 'Usage Term',
    entity: 'project_creator',
    type: 'string',
    options: [],
  },
];

const CREATOR_TRACKING_DELIVERABLE_FIELDS = PROJECT_DELIVERABLE_TRACKING_ENTITY_FIELDS.map((field) => ({ ...field }));

const ACTUAL_METRIC_CUSTOM_PROPERTY_KEYS = new Set(['actual_views_impressions_custom_property', 'actual_engagements_custom_property']);

const toNumberOrEmpty = (value) => {
  if (value === '' || value == null || value === 'null' || value === 'undefined') {
    return '';
  }

  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : '';
};

const CREATOR_TRACKING_VISIBLE_FIELDS = [
  normalizeCustomPropertyField('Tracking Status'),
  'username.display_value',
  'social_channels.display_value',
  'email.display_value',
  'contract_value.display_value',
  'asset_name.display_value',
  normalizeCustomPropertyField('Usage Term'),
  'content_rights_expiration_date.display_value',
  'contract_status.display_value',
  normalizeCustomPropertyField('Shipping Info Rec'),
  normalizeCustomPropertyField('Product Received'),
  normalizeCustomPropertyField('Concept Received'),
  normalizeCustomPropertyField('Concept Feedback'),
  normalizeCustomPropertyField('Revised Concept Received'),
  'confirmed.display_value',
  normalizeCustomPropertyField('First Draft Submission Date'),
  normalizeCustomPropertyField('First Draft Received'),
  normalizeCustomPropertyField('Content Feedback Shared'),
  normalizeCustomPropertyField('Final Content Revised Received'),
  normalizeCustomPropertyField('Content Approved'),
  'target_live_date.display_value',
  'live_date_custom_property.display_value',
];

const CREATOR_TRACKING_PINNED_FIELDS = [normalizeCustomPropertyField('Tracking Status'), 'username.display_value'];

const CREATOR_TRACKING_CONTRACT_STATUS_VALUES = ProjectsHelpers.getClientStatusOptions()
  .filter((status) => ['Negotiating', 'Drafting Contract', 'Out For Signature', 'Redlines', 'Signed'].includes(status.label))
  .map((status) => status.value);

const normalizeEntityMappingFieldName = (fieldName = '') => String(fieldName).trim().toLowerCase();

const hasEntityMapping = (mappings = [], fieldName) => {
  const normalizedFieldName = normalizeEntityMappingFieldName(fieldName);
  return mappings.some((mapping) => normalizeEntityMappingFieldName(mapping.field) === normalizedFieldName);
};

const upsertEntityMapping = (mappings = [], mapping) => {
  const normalizedFieldName = normalizeEntityMappingFieldName(mapping.field);
  const existingIndex = mappings.findIndex((item) => normalizeEntityMappingFieldName(item.field) === normalizedFieldName);

  if (existingIndex === -1) {
    mappings.push(mapping);
    return;
  }

  mappings.splice(existingIndex, 1, mapping);
};

const reorderDeliverableViews = (views = []) => {
  const remainingViews = [...views];
  const orderedViews = [];

  [CREATOR_TRACKING_VIEW_NAME, 'Internal Sheet', CONTENT_SUBMITTED_VIEW_NAME].forEach((name) => {
    const viewIndex = remainingViews.findIndex((view) => view.name === name);
    if (viewIndex !== -1) {
      orderedViews.push(remainingViews.splice(viewIndex, 1)[0]);
    }
  });

  return [...orderedViews, ...remainingViews];
};

const buildCreatorTrackingColumns = (columns = [], mergedEntities = [], creatorMappings = []) => {
  const baseColumns = columns.filter((column) => column.field && column.field !== 'addColumn');
  const existingFieldIds = new Set(baseColumns.map((column) => column.field));
  const customColumnSettings = {
    entityOrigin: 'project_deliverables',
    isCreatorsEntity: creatorMappings.length > 0,
  };
  const customColumns = mergedEntities
    .map((mapping) => ProjectsHelpers.formatCustomColumn(mapping, customColumnSettings, true))
    .filter((column) => column.field && !existingFieldIds.has(column.field));

  return [...baseColumns, ...customColumns];
};

const buildCreatorTrackingViewPayload = (columns = [], projectID) => {
  const visibleFields = new Set(CREATOR_TRACKING_VISIBLE_FIELDS);
  const allFieldIds = columns.filter((column) => column.field).map((column) => column.field);
  const orderedColIds = [...CREATOR_TRACKING_VISIBLE_FIELDS, ...allFieldIds.filter((field) => !visibleFields.has(field))];
  const hiddenFields = columns
    .filter((column) => column.field && !visibleFields.has(column.field))
    .map((column) => ({ headerName: column.headerName }));

  return {
    project_id: Number(projectID),
    name: CREATOR_TRACKING_VIEW_NAME,
    base: 'deliverables',
    display_mode: 'grid',
    shared: false,
    locked: false,
    grouping: JSON.stringify([]),
    filter: JSON.stringify({
      'contract_status.display_value': {
        filterType: 'set',
        values: CREATOR_TRACKING_CONTRACT_STATUS_VALUES,
      },
    }),
    sort: JSON.stringify([]),
    hidden_fields: JSON.stringify(hiddenFields),
    blocked: JSON.stringify([]),
    column_state: JSON.stringify({
      columnOrder: {
        orderedColIds,
      },
      columnPinning: {
        leftColIds: CREATOR_TRACKING_PINNED_FIELDS,
        rightColIds: [],
      },
    }),
    custom_properties: [{ key: 'Last Used', value: ['false'] }],
  };
};

export const useProjectDeliverablesStore = defineStore({
  id: 'projectDeliverables',
  state: () => ({
    showGrid: true,
    isLoadingDeliverables: false,
    deliverables: [],
    filteredSortedDeliverables: [],
    filteredCardData: [],
    activeDeliverables: false,
    entityMappings: {
      creators: [],
      deliverables: [],
    },
    views: [],
    filterOptions: [],
    filterCardOptions: null,
    activeView: {
      name: 'Base Grid',
      base: 'deliverables',
      grouping: [],
      sort: [],
      filter: [],
      hidden_fields: [],
      column_state: [],
      custom_properties: [],
    },
    projectHelpers: ProjectsHelpers,
    uploadDeliverbleAssetsDialog: false,
    error: null,
  }),
  getters: {
    products(state) {
      return useProjectStore().products;
    },
    briefing_files(state) {
      return useProjectStore().briefFiles;
    },
    mergedEntities(state) {
      return [...state.entityMappings.creators, ...state.entityMappings.deliverables];
    },
    cardDeliverables(state) {
      if (state.activeDeliverables) {
        const mappedDeliverables = state.deliverables.map((deliverable) => ({
          ...deliverable.current_asset_version.display_value,
          name: deliverable?.asset_name?.display_value || deliverable?.name,
          project_id: Number(useProjectStore().activeProject.id),
          deliverable_id: Number(deliverable.project_deliverable_id),
          creator_channels: deliverable.creator.display_value
            ? [
                {
                  creator: deliverable.creator.display_value,
                },
              ]
            : null,
          status: deliverable.status.display_value,
          products: deliverable.products.display_value,
          destinations: deliverable.destinations.display_value.map((destination) => destination.account_social_channel),
          rights: {
            content_rights: deliverable.content_rights.display_value,
          },
          assets: deliverable.assets.display_value,
          decision: deliverable.decision.display_value,
          current_asset_version_uploaded_at: deliverable?.current_asset_version_uploaded_at.display_value ? new Date(deliverable.current_asset_version_uploaded_at.display_value) : null,
          favorite: deliverable.favorite.display_value,
          version: deliverable.asset_version_number.display_value,
        }));

        return mappedDeliverables.sort((a, b) => new Date(b.current_asset_version_uploaded_at) - new Date(a.current_asset_version_uploaded_at));
      } else {
        return [];
      }
    },
    columns(state) {
      // generate columns based on the project's entity mappings'
      const customColumnSettings = {
        entityOrigin: 'project_deliverables',
        isCreatorsEntity: state.entityMappings.creators.length > 0,
      };
      const customColumns = this.mergedEntities.map((mapping) => ProjectsHelpers.formatCustomColumn(mapping, customColumnSettings, useProjectStore().isCreatorDetailsViewActive));

      const addColumn = {
        headerName: 'addColumn',
        headerComponent: AddColumnHeader,
        cellRenderer: BlankCellViewer,
        field: 'addColumn',
        width: 150,
        sort: false,
        filter: false,
        suppressHeaderMenuButton: true, // Disable any context menu on this column
        suppressMovable: true, // Prevent this column from being moved
        suppressColumnsToolPanel: true, // Hide the column in the columns panel
        lockPosition: 'right',
        suppressSizeToFit: true,
        suppressHeaderContextMenu: true,
      };

      let data = [
        {
          field: 'asset_name.display_value',
          headerName: 'Asset Name',
          headerClass: 'header-string',
          sortable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          editable: useProjectStore().isCreatorDetailsViewActive,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          cellEditorParams: {
            key: 'asset_name',
          },
          context: {},
        },
        {
          field: 'username.display_value',
          headerName: 'Name',
          headerClass: 'header-user',
          sortable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
          },
          editable: false,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          cellEditorParams: {
            key: 'username',
          },
          context: {
            renderViewType: 'user',
            renderEditType: 'creator-single-select',
            singleSelect: true,
            returnObject: true,
            dropdownSelections: [],
          },
        },
        {
          field: 'destinations.display_value',
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.account_social_channel?.type ?? '').join(', ');
            }
            return value ?? '';
          },
          headerName: 'Posting to',
          headerClass: 'header-posting',
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          filter: 'agSetColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getPostingToSocialChannelsOptions().map((posting) => posting?.value),
            valueFormatter: (params) => {
              return params.data;
            },
            keyCreator: (params) => {
              return params.value || [];
            },
          },
          filterValueGetter: (params) => {
            return params.data.destinations.display_value.map((destination) => destination?.account_social_channel.type);
          },
          cellEditorParams: {
            key: 'destinations',
          },
          context: {
            customField: true,
            renderViewType: 'destination',
            renderEditType: 'destination',
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              alert('Delete action is not supported for Posting to, please select a post destination to delete it.');

              params.event.preventDefault();
              return true;
            }
          },
        },
        {
          field: 'status.display_value',
          headerName: 'Production Status',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ProjectsHelpers.getDeliverableStatusOptions().map((status) => status.value),
          },
          cellEditorParams: {
            key: 'status',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            dropdownSelections: ProjectsHelpers.getDeliverableStatusOptions(),
          },
        },
        {
          field: 'products.display_value',
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.name).join(', ');
            }
            return value ?? '';
          },
          headerName: 'Product Attached',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: state.products.map((product) => product.name),
          },
          filterValueGetter: (params) => {
            return params.data.products.display_value.map((products) => products.name);
          },
          cellEditorParams: {
            key: 'products',
          },
          context: {
            renderViewType: 'products',
            renderEditType: 'products',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: state.products,
            payloadTransformer: (payload) => {
              return payload ? payload.map((product) => Number(product.id)) : [];
            },
          },
        },
        {
          field: 'contract_status.display_value',
          headerName: 'Contract Status',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          suppressPaste: !useProjectStore().isCreatorDetailsViewActive,
          suppressFillHandle: !useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getClientStatusOptions().map((status) => status.value),
          },
          cellEditorParams: {
            key: 'contract_status',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
          },
        },
        {
          field: 'notes.display_value',
          headerName: 'Notes',
          headerClass: 'header-string',
          sortable: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.notes.display_value ? 'Yes' : 'No';
          },
          editable: true,
          cellEditorParams: {
            key: 'notes',
            maxLength: 10000,
          },
          cellEditor: 'agLargeTextCellEditor',
          context: {},
        },
        {
          field: 'assets.display_value',
          headerName: 'Assets',
          headerClass: 'header-assets',
          sortable: false,
          singleClickEdit: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          valueGetter: (params) => {
            // Check if assets.display_value has any items
            return params.data.assets?.display_value?.length > 0 ? 'Yes' : 'No';
          },
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          cellEditorParams: {
            key: 'assets',
          },
          context: {
            customField: true,
            renderViewType: 'asset-uploader',
            renderEditType: 'asset-uploader',
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              alert('Delete action is not supported for assets, please select an asset to delete it.');

              params.event.preventDefault();
              return true;
            }
          },
        },
        {
          field: 'decision.display_value',
          headerName: 'Decision (Client)',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getDecisionOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'decision',
          },
          context: {
            renderViewType: 'decision',
          },
        },
        {
          field: 'favorite.display_value',
          keyCreator: (params) => {
            // Used for grouping - returns the same values as filterValueGetter
            const favoriteValue = params.data?.favorite?.display_value;
            return favoriteValue === true ? 'Recommended' : 'Others';
          },
          headerName: 'Favs',
          headerClass: 'header-favorite',
          sortable: true,
          editable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            // Don't specify values array - let AG Grid derive from filterValueGetter
            // This avoids the "Key Creator with primitives" error
          },
          filterValueGetter: (params) => {
            // Access the favorite value directly from the data structure
            // The field is 'favorite.display_value', so we need to check params.data.favorite.display_value
            const favoriteValue = params.data?.favorite?.display_value;
            // Check if favorite is true (boolean) - anything else (false, null, undefined, '') is 'Others'
            // Must return the same values as keyCreator for grouping to work correctly
            return favoriteValue === true ? 'Recommended' : 'Others';
          },
          cellEditorParams: {
            key: 'favorite',
          },
          context: {
            renderViewType: 'favorite',
          },
        },
        {
          field: 'content_rights.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Rights',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getRightsOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'content_rights',
          },
          context: {
            renderViewType: 'chip',
            hideNoDate: true,
          },
        },
        {
          field: 'content_rights_expiration_date.display_value',
          headerName: 'Exp. Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.content_rights_expiration_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'content_rights_expiration_date',
          },
          context: {
            renderViewType: 'date',
          },
        },
        {
          field: 'contract_value.display_value',
          cellRenderer: (params) => {
            return params.data?.contract_value?.display_value ? `$${Helpers.formatNumberWithCommas(Number(params.data.contract_value?.display_value))}` : '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          headerName: 'Final Rate',
          headerClass: 'header-currency',
          singleClickEdit: true,
          sortable: true,
          editable: false,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'contract_value',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            filterType: 'currency',
            renderEditType: 'currency',
          },
        },
        {
          field: 'due_date.display_value',
          headerName: 'Due Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.due_date.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'due_date',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
          },
        },
        /*{
          field: 'briefing_files.display_value',
          keyCreator: (params) => params.value?.map((briefing_file) => briefing_file.name).join(', ') || '',
          headerName: 'Briefing Files',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: state.briefing_files?.map((briefing_file) => briefing_file.filename) || [],
          },
          filterValueGetter: (params) => {
            return params.data.briefing_files?.display_value?.map((briefing_file) => briefing_file.filename) || [];
          },
          cellEditorParams: {
            key: 'briefing_files',
          },
          context: {
            renderViewType: 'briefing_files',
            renderEditType: 'briefing_files',
          },
        },*/
        {
          field: 'briefing_files.display_value',
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.name).join(', ');
            }
            return value ?? '';
          },
          headerName: 'Briefing Files',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            keyCreator: (params) => params.value,
            valueFormatter: (params) => params.value,
            values: state.briefing_files.map((briefing_file) => briefing_file.filename),
          },
          filterValueGetter: (params) => {
            return params.data.briefing_files.display_value.map((briefing_file) => briefing_file.filename);
          },
          cellEditorParams: {
            key: 'briefing_files',
          },
          valueGetter: (p) => {
            const bf = p.data.briefing_files || (p.data.briefing_files = {});
            const src = p.colDef?.context?.dropdownSelections ?? [];

            const byId = Object.create(null);
            for (const f of src) byId[Number(f.id ?? f?.raw?.id)] = f;

            let stored = bf.display_value ?? [];
            let objs;

            if (stored.length && typeof stored[0] === 'object') {
              objs = stored;
              if (!bf.__globals) {
                bf.__globals = objs.filter((o) => (o?.deliverable_id ?? o?.raw?.deliverable_id) == null);
              }
              bf.display_value = objs
                .filter((o) => (o?.deliverable_id ?? o?.raw?.deliverable_id) != null)
                .map((o) => Number(o?.id ?? o?.raw?.id))
                .filter(Boolean);
              stored = bf.display_value;
            } else {
              objs = Array.isArray(stored) ? stored.map((id) => byId[Number(id)]).filter(Boolean) : [];
            }

            const globals = Array.isArray(bf.__globals) ? bf.__globals : [];
            const locals = objs;

            // stamp id sets for the editor (you already use these)
            bf.__globals_ids = new Set(globals.map((o) => Number(o?.id ?? o?.raw?.id)));
            bf.__locals_ids = new Set(locals.map((o) => Number(o?.id ?? o?.raw?.id)));

            // ⬇️ tag globals for the submit transformer
            const taggedGlobals = globals.map((g) => ({ ...g, __isGlobal: true }));

            // return display list (globals first, then locals), dedup by id
            const seen = new Set(),
              out = [];
            [...taggedGlobals, ...locals].forEach((o) => {
              const id = Number(o?.id ?? o?.raw?.id);
              if (id && !seen.has(id)) {
                seen.add(id);
                out.push(o);
              }
            });
            return out;
          },
          valueSetter: (p) => {
            // normalize to numeric IDs
            const toId = (v) => (typeof v === 'object' ? Number(v?.id ?? v?.raw?.id) : Number(v));
            let ids = Array.isArray(p.newValue) ? p.newValue.map(toId).filter(Boolean) : [];

            // remove globals using row-stamped set from valueGetter
            const globals = p.data?.briefing_files?.__globals_ids;
            if (globals instanceof Set) {
              ids = ids.filter((id) => !globals.has(Number(id)));
            }

            p.data.briefing_files = { ...(p.data.briefing_files || {}), display_value: ids };
            return true;
          },
          context: {
            renderViewType: 'briefing_files',
            renderEditType: 'briefing_files',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: state.briefing_files,
            isDeliverables: true,
            payloadTransformer: (payload) =>
              Array.isArray(payload)
                ? payload
                    .filter((v) => !(typeof v === 'object' && v.__isGlobal === true)) // drop globals
                    .map((v) => (typeof v === 'object' ? Number(v?.id ?? v?.raw?.id) : Number(v)))
                    .filter(Boolean)
                : [],
          },
        },
        {
          field: 'instagram_reach.display_value',
          headerName: 'IG Reach',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_reach?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.instagram_reach?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_reach',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'tiktok_reach.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT Reach',
          cellRenderer: (params) => {
            return params.data?.tiktok_reach?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.tiktok_reach?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_reach',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'youtube_reach.display_value',
          headerClass: 'header-youtube',
          headerName: 'YT Reach',
          cellRenderer: (params) => {
            return params.data?.youtube_reach?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.youtube_reach?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_reach',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'instagram_impressions.display_value',
          headerName: 'IG Impressions',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_impressions?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.instagram_impressions?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_impressions',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'tiktok_impressions.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT Impressions',
          cellRenderer: (params) => {
            return params.data?.tiktok_impressions?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.tiktok_impressions?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_impressions',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'youtube_impressions.display_value',
          headerName: 'YT Impressions',
          headerClass: 'header-youtube',
          cellRenderer: (params) => {
            return params.data?.youtube_impressions?.display_value ? Helpers.formatNumberWithCommas(Number(params.data?.youtube_impressions?.display_value)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          singleClickEdit: true,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_impressions',
            precision: 0,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
          },
        },
        {
          field: 'total_reach',
          headerName: 'Total Reach',
          headerClass: 'header-formula',
          cellRenderer: (params) => {
            // add instagram, tiktok, and youtube reach
            const reach = Number(params.data?.instagram_reach?.display_value) + Number(params.data?.tiktok_reach?.display_value) + Number(params.data?.youtube_reach?.display_value);
            return reach ? Helpers.formatNumberWithCommas(Number(reach)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'total_reach',
          },
          context: {
            filterType: 'number',
            renderViewType: 'custom',
          },
        },
        {
          field: 'total_estimated_impressions',
          headerClass: 'header-formula',
          headerName: 'Total Est. Impressions',
          cellRenderer: (params) => {
            // add instagram, tiktok, and youtube impressions
            const impressions =
              Number(params.data?.instagram_impressions?.display_value) + Number(params.data?.tiktok_impressions?.display_value) + Number(params.data?.youtube_impressions?.display_value);
            return impressions ? Helpers.formatNumberWithCommas(Number(impressions)) : '';
          },
          keyCreator: (params) => (params.value ? Helpers.formatNumberWithCommas(Number(params.value)) : ''),
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
          },
          cellEditorParams: {
            key: 'total_estimated_impressions',
          },
          context: {
            renderViewType: 'custom',
          },
        },
        {
          field: 'payee.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Payee',
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getPayeeOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'payee',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: false,
            dropdownSelections: ProjectsHelpers.getPayeeOptions(),
          },
        },
        {
          field: 'actual_views_impressions_custom_property.display_value',
          valueGetter: (params) => {
            const val = params.data?.actual_views_impressions_custom_property?.display_value;
            if (val === '' || val === null || val === undefined) {
              return '';
            }
            if (typeof val === 'number' && isNaN(val)) {
              return '';
            }
            const numVal = Number(val);
            return !isNaN(numVal) && isFinite(numVal) ? numVal : '';
          },
          cellRenderer: ({ data }) => {
            const val = data?.actual_views_impressions_custom_property?.display_value;
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              const numVal = Number(val);
              if (!isNaN(numVal) && isFinite(numVal)) {
                return Helpers.formatNumberWithCommas(numVal);
              }
            }
            return '';
          },
          keyCreator: (params) => {
            const val = params.value;
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              return Helpers.formatNumberWithCommas(Number(val));
            }
            return '';
          },
          headerName: '# of Views/Impressions',
          headerClass: 'header-number',
          singleClickEdit: true,
          cellDataType: 'number',
          sortable: true,
          editable: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'actual_views_impressions_custom_property',
            precision: 0,
            step: 1,
            showStepperButtons: true,
          },
          context: {
            turnOffEndpoint: false,
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              if (payload === null || payload === undefined || payload === '' || isNaN(payload)) {
                return '';
              }
              return String(payload);
            },
          },
        },
        {
          field: 'target_live_date.display_value',
          headerName: 'Target Live Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.target_live_date?.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'target_live_date',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
          },
        },
        {
          field: 'live_date_custom_property.display_value',
          headerName: 'Actual Live Date',
          headerClass: 'header-date',
          singleClickEdit: true,
          sortable: true,
          editable: useProjectStore().isCreatorDetailsViewActive,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.live_date_custom_property?.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'live_date_custom_property',
          },
          context: {
            renderViewType: 'date',
            renderEditType: 'date',
            payloadTransformer: (payload) => {
              if (!payload) return null;
              if (payload instanceof Date) return payload.toISOString();
              return payload;
            },
          },
        },
        {
          field: 'actual_engagements_custom_property.display_value',
          valueGetter: (params) => {
            const val = params.data?.actual_engagements_custom_property?.display_value;
            if (val === '' || val === null || val === undefined) {
              return '';
            }
            if (typeof val === 'number' && isNaN(val)) {
              return '';
            }
            const numVal = Number(val);
            return !isNaN(numVal) && isFinite(numVal) ? numVal : '';
          },
          cellRenderer: ({ data }) => {
            const val = data?.actual_engagements_custom_property?.display_value;
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              const numVal = Number(val);
              if (!isNaN(numVal) && isFinite(numVal)) {
                return Helpers.formatNumberWithCommas(numVal);
              }
            }
            return '';
          },
          keyCreator: (params) => {
            const val = params.value;
            if (val !== '' && val !== null && val !== undefined && !isNaN(val)) {
              return Helpers.formatNumberWithCommas(Number(val));
            }
            return '';
          },
          headerName: '# of Engagements',
          headerClass: 'header-number',
          singleClickEdit: true,
          cellDataType: 'number',
          sortable: true,
          editable: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
            defaultToNothingSelected: true,
          },
          cellEditorParams: {
            key: 'actual_engagements_custom_property',
            precision: 0,
            step: 1,
            showStepperButtons: true,
          },
          context: {
            turnOffEndpoint: false,
            filterType: 'number',
            renderEditType: 'integer',
            payloadTransformer: (payload) => {
              if (payload === null || payload === undefined || payload === '' || isNaN(payload)) {
                return '';
              }
              return String(payload);
            },
          },
        },
        {
          field: 'documents.display_value',
          keyCreator: (params) => {
            return params.value.length > 0 ? 'Yes' : 'No';
          },
          headerName: 'Documents',
          headerClass: 'header-file',
          sortable: true,
          editable: true,
          filter: true,
          suppressPaste: false,
          suppressFillHandle: false,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.documents.display_value.length > 0 ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'documents',
          },
          context: {
            turnOffEndpoint: true, // this prevents the use of the endpoint to fetch data (turn this off until a sync endpoint is implemented)
            renderViewType: 'documents',
            renderEditType: 'documents',
            multiSelect: true,
            returnObject: true,
            dropdownSelections: [],
            // payloadTransformer: (payload) => {
            //   return payload ? payload.map((deliverable) => Number(deliverable.id)) : [];
            // },
          },
          suppressKeyboardEvent: (params) => {
            const key = params.event.key;
            if (key === 'Delete') {
              // Prevent the deletion of the row
              params.event.preventDefault();
              alert('Delete action is not supported for documents, please select a document to delete it.');
              return true;
            }
          },
        },
        {
          field: 'confirmed.display_value',
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          headerClass: 'header-checkbox',
          headerName: 'Concept Approved',
          singleClickEdit: true,
          sortable: true,
          editable: true,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.confirmed.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'confirmed',
          },
          context: {},
        },
        {
          field: 'initial_quote.display_value',
          cellRenderer: (params) => {
            return params.data?.initial_quote.display_value ? `$${Helpers.formatNumberWithCommas(Number(params.data.initial_quote.display_value))}` : '';
          },
          keyCreator: (params) => (params.value ? `$${Helpers.formatNumberWithCommas(Number(params.value))}` : '$0'),
          headerName: 'Initial Quote',
          headerClass: 'header-currency',
          singleClickEdit: true,
          sortable: true,
          editable: false,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'initial_quote',
            precision: 2,
            step: 0.01,
            showStepperButtons: true,
          },
          context: {
            filterType: 'currency',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'savings.display_value',
          cellRenderer: (params) => {
            if (params.data?.initial_quote.display_value && params.data?.contract_value.display_value) {
              const savings = Number(params.data.initial_quote.display_value) - Number(params.data.contract_value.display_value);
              // Round to 2 decimal places
              const rounded = Math.round(savings * 100) / 100;
              return `$${Helpers.formatNumberWithCommas(rounded)}`;
            }
            return '';
          },
          valueGetter: (params) => {
            if (params.data?.initial_quote.display_value && params.data?.contract_value.display_value) {
              const savings = Number(params.data.initial_quote.display_value) - Number(params.data.contract_value.display_value);
              // Round to 2 decimal places
              const rounded = Math.round(savings * 100) / 100;
              return `$${Helpers.formatNumberWithCommas(rounded)}`;
            }
            return '$0';
          },
          headerName: 'Savings',
          headerClass: 'header-formula',
          sortable: true,
          editable: false,
          filter: 'agNumberColumnFilter',
          filterParams: {
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'savings',
          },
          context: {
            turnOffEndpoint: true,
            filterType: 'currency',
            renderViewType: 'custom',
          },
        },
        {
          field: 'creator_notes.display_value',
          headerName: 'Summary',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.creator_notes.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'notes',
            maxLength: 10000,
          },
          cellEditor: 'agLargeTextCellEditor',
          context: {},
        },
        {
          field: 'phone.display_value',
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          headerName: 'Phone #',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.phone.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'phone',
          },
          cellRenderer: (params) => {
            return params.data?.phone.display_value ?? '';
          },
          context: {},
        },
        {
          field: 'email.display_value',
          headerName: 'Contact Email',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.email.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'email',
          },
          cellRenderer: (params) => {
            return params.data?.email.display_value ? `<a href="mailto:${params.data.email.display_value}" target="_blank">${params.data.email.display_value}</a>` : '';
          },
          context: {},
        },
        {
          field: 'social_channels.display_value',
          headerName: 'Handles',
          headerClass: 'header-web',
          sortable: true,
          editable: false,
          filter: true,
          suppressPaste: true,
          suppressFillHandle: true,
          keyCreator: ({ value }) => {
            if (Array.isArray(value)) {
              return value.map((b) => b.type).join(', ');
            }
            return value ?? '';
          },
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getPostingToSocialChannelsOptions().map((posting) => posting?.value),
            valueFormatter: (params) => {
              return params.data;
            },
            keyCreator: (params) => {
              return params.value || [];
            },
          },
          filterValueGetter: (params) => {
            return params.data.social_channels.display_value.map((social) => social?.type);
          },
          cellEditorParams: {
            key: 'social_channels',
          },
          context: {
            customField: true,
            renderViewType: 'social_channels',
          },
        },
        /*{
          field: 'documents.display_value',
          headerName: 'Documents',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            keyCreator: (params) => params.value,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.documents.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'documents',
          },
          context: {},
        },*/
        {
          field: 'example_content.display_value',
          cellEditor: 'agLargeTextCellEditor',
          headerName: 'Example Content',
          headerClass: 'header-url',
          sortable: true,
          editable: false,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.example_content.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'example_content',
            maxLength: 10000,
          },
          context: {
            renderViewType: 'example_content',
          },
        },
        {
          field: 'instagram_engagement_rate.display_value',
          headerName: 'IG ER',
          headerClass: 'header-instagram',
          cellRenderer: (params) => {
            return params.data?.instagram_engagement_rate?.display_value ? `${params.data?.instagram_engagement_rate.display_value}%` : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: false,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'instagram_engagement_rate',
            precision: 2,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'tiktok_engagement_rate.display_value',
          headerClass: 'header-tiktok',
          headerName: 'TT ER',
          cellRenderer: (params) => {
            return params.data?.tiktok_engagement_rate?.display_value ? `${params.data?.tiktok_engagement_rate.display_value}%` : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: false,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'tiktok_engagement_rate',
            precision: 2,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'youtube_engagement_rate.display_value',
          headerName: 'YT ER',
          headerClass: 'header-youtube',
          cellRenderer: (params) => {
            return params.data?.youtube_engagement_rate?.display_value ? `${params.data?.youtube_engagement_rate.display_value}%` : '';
          },
          keyCreator: (params) => (params.value ? `${params.value}%` : ''),
          sortable: true,
          editable: false,
          cellDataType: 'number',
          filter: 'agNumberColumnFilter',
          singleClickEdit: true,
          filterParams: {
            defaultToNothingSelected: true,
            valueFormatter: (params) => params.value ?? 0,
          },
          cellEditorParams: {
            key: 'youtube_engagement_rate',
            precision: 2,
            showStepperButtons: false,
          },
          context: {
            filterType: 'number',
            payloadTransformer: (payload) => {
              return Number(payload);
            },
          },
        },
        {
          field: 'verticals.display_value',
          headerName: 'Verticals',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getVerticalOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'verticals',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: true,
            dropdownSelections: ProjectsHelpers.getVerticalOptions(),
          },
        },
        {
          field: 'gender.display_value',
          headerName: 'Gender',
          headerClass: 'header-dropdown',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getGenderOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'gender',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            dropdownSelections: ProjectsHelpers.getGenderOptions(),
          },
        },
        {
          field: 'ethnicity.display_value',
          headerClass: 'header-dropdown',
          headerName: 'Ethnicity',
          sortable: true,
          editable: false,
          filter: true,
          filterParams: {
            defaultToNothingSelected: true,
            values: ProjectsHelpers.getEthnicityOptions().map((option) => option?.value),
          },
          cellEditorParams: {
            key: 'ethnicity',
          },
          context: {
            renderViewType: 'chip',
            renderEditType: 'single-select',
            multiSelect: true,
            dropdownSelections: ProjectsHelpers.getEthnicityOptions(),
          },
        },
        {
          field: 'client_notes.display_value',
          headerName: 'Client Feedback',
          headerClass: 'header-string',
          sortable: true,
          editable: false,
          filter: true,
          keyCreator: (params) => (params.value ? 'Yes' : 'No'),
          filterParams: {
            valueFormatter: (params) => params.value,
            defaultToNothingSelected: true,
            values: ['Yes', 'No'],
          },
          filterValueGetter: (params) => {
            return params.data.client_notes.display_value ? 'Yes' : 'No';
          },
          cellEditorParams: {
            key: 'client_notes',
            maxLength: 10000,
          },
          cellEditor: 'agLargeTextCellEditor',
          context: {},
        },
      ];

      if (useProjectStore().isCreatorDetailsViewActive) {
        // only add the add column header if we have access to the creator details view as well.
        data.push(...customColumns, addColumn);
      } else {
        // only add the custom columns that an invited creator can see
        const includedCustomFields = ['Link to Reporting (URL)', 'Link to CTA (URL)', 'Tiktok Upload Links (URL)', 'Spark Code'];
        data.push(...customColumns.filter((column) => includedCustomFields.includes(column.headerName)));
      }

      return data;
    },
  },
  actions: {
    setDeliverables(deliverables) {
      if (deliverables && deliverables.length > 0) {
        this.activeDeliverables = true;
        this.deliverables = this.generateDeliverablesGridData(deliverables, this.mergedEntities);
      } else {
        this.deliverables = ProjectsHelpers.generateEmptyGridData('deliverables', 5, 0);
      }
    },

    setFilterOptions(column, options) {
      this.filterOptions.push({
        field: column.field,
        type: column.type,
        filterType: column?.context?.filterType,
        columnHeaderName: column.headerName,
        options: options,
        key: column?.cellEditorParams?.key,
      });
    },

    setFilteredSortedDeliverables(data) {
      this.filteredSortedDeliverables = data;
    },

    setFilteredCardData(filterOptions) {
      this.filteredCardData = this.cardDeliverables.filter((card) => {
        return Object.entries(filterOptions).every(([field, filter]) => {
          switch (field) {
            case 'name_of_partner':
              const creators = card.creator_channels ? card.creator_channels.map((channel) => channel.creator?.username).filter(Boolean) : [];
              return filter.values.some((value) => creators.includes(value));
            case 'product_name':
              const products = card.products ? card.products.map((product) => product.name).filter(Boolean) : [];
              return filter.values.some((value) => products.includes(value));
            case 'destination_type':
              const channels = card.destinations?.length > 0 ? card.destinations.map((destination) => destination.type).filter(Boolean) : [];
              return filter.values.some((value) => channels.includes(value));
            case 'status':
              return card?.status ? filter.values.includes(ProjectsHelpers.formatValueString(card.status)) : false;
            case 'rights':
              return card?.rights?.content_rights ? filter.values.includes(ProjectsHelpers.formatValueString(card.rights.content_rights)) : false;
            case 'asset_count':
              if (filter.values.includes('Yes')) {
                return card.assets.length > 0;
              }

              if (filter.values.includes('No')) {
                return card.assets.length === 0;
              }
              return true; // For now, just assume if asset_count is set, it's Yes'
            case 'decisions':
              return card.decision ? filter.values.includes(card.decision) : false;
            case 'favorite':
              if (filter.values.includes('Recommended')) {
                return card.favorite;
              }

              if (filter.values.includes('Others')) {
                return !card.favorite;
              }

              return true;

            default:
              return false;
          }
        });
      });
    },

    setViews(views) {
      if (views && views.length > 0) {
        this.views = reorderDeliverableViews(views);
      }
    },

    setActiveView(view) {
      if (view) {
        this.activeView = view;
      }
    },

    async setViewShared(projectViewID, shared = true) {
      let res = await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, { id: projectViewID, shared: shared });
      if (res.data.data.updateView?.id) {
        this.activeView.shared = res.data.data.updateView.shared;
      }
    },

    async ensureCreatorTrackingSetup() {
      const accountID = Number(useProjectStore().activeAccount.id);
      const projectID = Number(useProjectStore().currentProjectID);
      const missingCreatorMappings = CREATOR_TRACKING_CREATOR_FIELDS.filter((mapping) => !hasEntityMapping(this.entityMappings.creators, mapping.field));
      const missingDeliverableMappings = CREATOR_TRACKING_DELIVERABLE_FIELDS.filter((mapping) => !hasEntityMapping(this.entityMappings.deliverables, mapping.field));
      const missingMappings = [...missingCreatorMappings, ...missingDeliverableMappings];

      if (missingMappings.length > 0) {
        const createEntityResults = await Promise.allSettled(
          missingMappings.map(async (mapping) => {
            const res = await API.sendQuery('/graphql/account', CREATE_ENTITY, {
              account_id: accountID,
              project_id: projectID,
              field_name: mapping.field,
              entity_name: mapping.entity,
              field_type: mapping.type,
              options: mapping.options,
            });

            return res.data?.data?.createEntity ?? null;
          })
        );

        createEntityResults
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value)
          .filter(Boolean)
          .forEach((mapping) => {
          if (mapping.entity === 'project_creator') {
            upsertEntityMapping(this.entityMappings.creators, mapping);
          }

          if (mapping.entity === 'project_deliverable') {
            upsertEntityMapping(this.entityMappings.deliverables, mapping);
          }
          });
      }

      if (!this.views.some((view) => view.name === CREATOR_TRACKING_VIEW_NAME)) {
        const creatorTrackingColumns = buildCreatorTrackingColumns(this.columns, this.mergedEntities, this.entityMappings.creators);
        const newView = await useProjectStore().createProjectView(buildCreatorTrackingViewPayload(creatorTrackingColumns, projectID));

        if (newView) {
          this.setViews([...this.views, newView]);
        }
      }
    },

    async initProjectDeliverables(isLoading = true) {
      this.isLoadingDeliverables = isLoading;
      this.filterOptions = []; // Clear previous filter options
      const projectID = Number(useProjectStore().currentProjectID);

      const [deliverablesEntities, creatorsEntities, deliverables, media] = await Promise.all([
        API.sendQuery('/graphql/project', GET_PROJECT_ENTITY_MAPPING_QUERY, { project_id: projectID, entity_name: ['project_deliverable'] }),
        API.sendQuery('/graphql/project', GET_PROJECT_ENTITY_MAPPING_QUERY, { project_id: projectID, entity_name: ['project_creator'] }),
        API.sendQuery('/graphql/project', GET_PROJECT_DELIVERABLES_QUERY, { project_id: projectID, project_creator_id: '', account_creator_id: '' }),
      ]);
      if (deliverablesEntities.data.data.entityMappings) {
        this.entityMappings.deliverables = deliverablesEntities.data.data.entityMappings;
      }

      if (creatorsEntities.data.data.entityMappings) {
        this.entityMappings.creators = creatorsEntities.data.data.entityMappings;
      }

      await this.ensureCreatorTrackingSetup();

      if (deliverables.data.data.deliverables) {
        this.setDeliverables(deliverables.data.data.deliverables);
      }

      this.isLoadingDeliverables = false;
    },

    async removeDeliverable(deliverable) {
      try {
        useProjectStore().isGridUpdating = true;
        this.deliverables = this.deliverables.filter((d) => Number(d.project_deliverable_id) !== Number(deliverable.project_deliverable_id));

        if (this.deliverables.length === 0) {
          this.activeDeliverables = false;
          // this.deliverables = ProjectHelpers.generateEmptyGridData('deliverables', 5, 0);
        }

        const res = await API.sendQuery('/graphql/project/deliverable', REMOVE_PROJECT_DELIVERABLE_MUTATION, {
          project_id: Number(useProjectStore().currentProjectID),
          project_deliverable_ids: [Number(deliverable.project_deliverable_id)],
        });

        if (res.data.data.deleteDeliverable) {
          console.log('Removed deliverable from project');
        }
      } catch (error) {
        console.log(error);
      } finally {
        useProjectStore().isGridUpdating = false;
      }
    },

    updateDeliverableProperty(deliverableId, propertyPath, newValue) {
      // Find the deliverable in the array
      const delIndex = this.deliverables.findIndex((d) => Number(d.project_deliverable_id) === Number(deliverableId));

      if (delIndex !== -1) {
        // Create a deep copy of the deliverable to ensure reactivity
        const updatedDeliverable = JSON.parse(JSON.stringify(this.deliverables[delIndex]));

        // Update the specific property
        const pathArray = propertyPath.split('.');
        let currentObj = updatedDeliverable;

        // Navigate to the parent object
        for (let i = 0; i < pathArray.length - 1; i++) {
          if (currentObj[pathArray[i]]) {
            currentObj = currentObj[pathArray[i]];
          }
        }

        // Update the final property
        const finalProperty = pathArray[pathArray.length - 1];
        if (currentObj && Object.prototype.hasOwnProperty.call(currentObj, finalProperty)) {
          currentObj[finalProperty] = newValue;
        }

        // Replace the entire deliverable object to trigger reactivity
        this.deliverables[delIndex] = updatedDeliverable;
      }
    },

    updateColumn(entityMappingId, column) {
      const foundColumn = this.entityMappings.deliverables.find((column) => Number(column.id) === Number(entityMappingId));
      if (foundColumn) {
        // add more properties to update in the future...
        foundColumn.field = column.headerName;
      }

      // we need to update all instances of this column in the deliverables grid data
      // this.deliverables.forEach((deliverable) => {
      //   deliverable.customProperties.forEach((customProperty) => {
      //     if (customProperty.key === column.originalHeaderName) {
      //       // remove the original header name from the custom properties
      //       delete customProperty.originalKey;

      //       customProperty.key = column.headerName;
      //       // update api
      //       API.sendQuery('/graphql/project/deliverable', UPDATE_PROJECT_DELIVERABLE_MUTATION, {
      //         project_id: Number(useProjectStore().currentProjectID),
      //         project_deliverable_id: Number(deliverable.id),
      //         custom_properties: [{ key: column.headerName, value: customProperty.value }],
      //       });
      //     }
      //   });
      // });
    },

    generateDeliverablesGridData(data = [], entityMappings = []) {
      if (data.length > 0) {
        return data.map((deliverable, index) => {
          // pluck out the custom properties and build the deliverable object
          const customProperties = deliverable.custom_properties;
          const creatorCustomProperties = deliverable.creator?.custom_properties;
          const deliverableCustomPropertyValuesByKey = new Map((customProperties ?? []).map((customProperty) => [customProperty.key, customProperty.value ?? []]));
          const accountCreatorCustomPropertyValuesByKey = new Map(
            (deliverable?.creator?.account_creator?.custom_properties ?? []).map((customProperty) => [customProperty.key, customProperty.value ?? []]),
          );
          const customPropertiesFields = {};
          const customEntityMappingsFields = {};

          if (customProperties && customProperties.length > 0) {
            customProperties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';
              let displayValue = customProperty.value && customProperty.value.length > 0 
                ? (customProperty.value.length > 1 ? customProperty.value : customProperty.value[0])
                : '';

              if (customProperty.value && customProperty.value[0] === 'true') {
                displayValue = true;
              }

              if (customProperty.value && customProperty.value[0] === 'false') {
                displayValue = false;
              }
              
              if (ACTUAL_METRIC_CUSTOM_PROPERTY_KEYS.has(key)) {
                displayValue = toNumberOrEmpty(displayValue);
              }

              return (customPropertiesFields[key] = {
                display_value: displayValue,
                endpoint: '/graphql/project/deliverable',
                query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
                key: key,
                originalKey: customProperty.key,
                custom_property: true,
                args: {
                  project_id: Number(useProjectStore().currentProjectID),
                  project_deliverable_id: Number(deliverable.id),
                  // project_creator_id: Number(deliverable.creator.account_creator.id),
                },
              });
            });
          }

          // just add the creator custom properties to the customPropertiesFields object (if they exist)
          // none editable for now
          if (creatorCustomProperties && creatorCustomProperties.length > 0) {
            creatorCustomProperties.map((customProperty) => {
              const key = customProperty.key.replaceAll(' ', '').toLowerCase() + '_custom_property';
              const customPropertyField = {
                display_value: customProperty.value.length > 1 ? customProperty.value : customProperty.value[0],
              };

              if (customProperty.key === 'Usage Term') {
                customPropertyField.endpoint = '/graphql/project/creator';
                customPropertyField.query = UPDATE_PROJECT_CREATOR_MUTATION;
                customPropertyField.key = key;
                customPropertyField.originalKey = customProperty.key;
                customPropertyField.custom_property = true;
                customPropertyField.args = {
                  project_id: Number(useProjectStore().currentProjectID),
                  project_creator_id: Number(deliverable.creator?.id),
                };
              }

              return (customPropertiesFields[key] = customPropertyField);
            });
          }

          // check for entity mappings and create deliverable objects
          entityMappings.map((mapping, index) => {
            const customEntityKey = `${mapping.field.replaceAll(' ', '').toLowerCase()}_custom_property`;
            // check if the custom entity key exists in the creator customPropertiesFields

            if (!customPropertiesFields[customEntityKey]) {
              return (customEntityMappingsFields[customEntityKey] = {
                display_value: mapping.entity === 'project_deliverable' ? deliverable[customEntityKey] : '',
                endpoint: mapping.entity === 'project_creator' ? '/graphql/project/creator' : '/graphql/project/deliverable',
                query: mapping.entity === 'project_creator' ? UPDATE_PROJECT_CREATOR_MUTATION : UPDATE_PROJECT_DELIVERABLE_MUTATION,
                key: customEntityKey,
                originalKey: mapping.field,
                custom_property: true,
                args: {
                  ...(mapping.entity === 'project_creator'
                    ? {
                        project_id: Number(useProjectStore().currentProjectID),
                        project_creator_id: Number(deliverable.creator?.id),
                      }
                    : {
                        project_id: Number(useProjectStore().currentProjectID),
                        project_deliverable_id: Number(deliverable.id),
                      }),
                },
              });
            }
          });

          const payee = accountCreatorCustomPropertyValuesByKey.get('payee')?.[0];

          const actualViewsImpressionsKey = 'actual_views_impressions_custom_property';
          const actualEngagementsKey = 'actual_engagements_custom_property';
          const conceptApprovedKey = 'conceptapproved_custom_property';
          const liveDateKey = 'live_date_custom_property';

          if (!customPropertiesFields[liveDateKey]) {
            customPropertiesFields[liveDateKey] = {
              display_value: deliverableCustomPropertyValuesByKey.get('live_date')?.[0] ?? '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: liveDateKey,
              originalKey: 'live_date',
              custom_property: true,
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            };
          }

          if (!customPropertiesFields[actualViewsImpressionsKey]) {
            customPropertiesFields[actualViewsImpressionsKey] = {
              display_value: '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: actualViewsImpressionsKey,
              originalKey: 'actual_views_impressions',
              custom_property: true,
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            };
          }

          if (!customPropertiesFields[actualEngagementsKey]) {
            customPropertiesFields[actualEngagementsKey] = {
              display_value: '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: actualEngagementsKey,
              originalKey: 'actual_engagements',
              custom_property: true,
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            };
          }

          if (!customPropertiesFields[conceptApprovedKey]) {
            customPropertiesFields[conceptApprovedKey] = {
              display_value: false,
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: conceptApprovedKey,
              originalKey: 'Concept Approved',
              custom_property: true,
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            };
          }

          const conceptApprovedField = customPropertiesFields[conceptApprovedKey];

          return {
            id: index + 1,
            customProperties: deliverable.custom_properties,
            creator_id: Number(deliverable.creator?.id),
            project_deliverable_id: Number(deliverable.id),
            asset_name: {
              display_value: deliverable.name ?? 'Deliverable',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'name',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            creator: {
              display_value: deliverable.creator?.account_creator ?? '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'creator',
              value_type: 'object',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            documents: {
              display_value: deliverable?.creator?.documents ? deliverable?.creator?.documents : [],
            },
            payee: {
              display_value: payee ? payee : '',
              endpoint: '/graphql/account/creator',
              query: UPDATE_CREATOR_MUTATION,
              key: 'payee',
              value_type: 'string',
              custom_property: true,
              originalKey: 'payee',
              args: {
                account_id: Number(useProjectStore().activeAccount.id),
                account_creator_id: Number(deliverable?.creator?.account_creator.id),
              },
            },
            avatar: {
              display_value: deliverable.creator?.account_creator?.avatar ?? '',
            },
            first_name: {
              display_value: deliverable.creator?.account_creator.first_name ?? '',
            },
            last_name: {
              display_value: deliverable.creator?.account_creator.last_name ?? '',
            },
            username: {
              display_value: deliverable.creator?.account_creator.username ?? '',
            },
            name: {
              display_value: deliverable.creator?.account_creator.first_name
                ? `${deliverable.creator.account_creator.first_name} ${deliverable.creator.account_creator?.last_name}`
                : (deliverable.creator?.account_creator?.username ?? ''),
            },
            email: {
              display_value: deliverable.creator?.account_creator.email ?? '',
            },
            favorite: {
              display_value: deliverable.creator?.favorite === null ? '' : deliverable?.creator?.favorite,
            },
            content_rights: {
              display_value: deliverable.creator?.contract?.content_rights ?? '',
            },
            content_rights_expiration_date: {
              display_value: deliverable.creator?.contract?.content_rights_expiration_date ?? null,
              endpoint: '/graphql/project/creator',
              query: UPDATE_CREATOR_CONTRACT_MUTATION,
              key: 'content_rights_expiration_date',
              value_type: 'date',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(deliverable.creator?.id),
              },
            },
            decision: {
              display_value: deliverable.creator?.decision === null ? '' : deliverable.creator?.decision,
            },
            social_channels: {
              display_value: deliverable.creator?.account_creator.social_channels,
            },
            youtube_reach: {
              display_value: deliverable.creator?.youtube_reach,
            },
            youtube_impressions: {
              display_value: deliverable.creator?.youtube_impressions,
            },
            youtube_engagement_rate: {
              display_value: deliverable.creator?.youtube_engagement_rate,
            },
            tiktok_reach: {
              display_value: deliverable.creator?.tiktok_reach,
            },
            tiktok_impressions: {
              display_value: deliverable.creator?.tiktok_impressions,
            },
            tiktok_engagement_rate: {
              display_value: deliverable.creator?.tiktok_engagement_rate,
            },
            instagram_reach: {
              display_value: deliverable.creator?.instagram_reach,
            },
            instagram_impressions: {
              display_value: deliverable.creator?.instagram_impressions,
            },
            contract_value: {
              display_value: deliverable.creator?.contract?.contract_value ? parseFloat(deliverable.creator.contract.contract_value) : '',
            },
            verticals: {
              display_value: deliverable.creator?.account_creator.verticals ?? [],
            },
            gender: {
              display_value: deliverable.creator?.account_creator.gender ?? '',
            },
            ethnicity: {
              display_value:
                deliverable.creator?.account_creator?.ethnicity.filter((item) => {
                  if (item !== '') {
                    return item;
                  }
                }) ?? [],
            },
            example_content: {
              display_value: deliverable.creator?.example_content ?? '',
            },
            phone: {
              display_value: deliverable.creator?.account_creator.phone ?? '',
            },
            creator_notes: {
              display_value: deliverable.creator?.notes ?? '',
            },
            client_notes: {
              display_value: deliverable.creator?.client_notes ?? '',
            },
            briefing_files: {
              // Show both global (no deliverable_id) and deliverable-specific files
              display_value: deliverable.creator?.briefing_files ? ProjectsHelpers.formatBriefingFiles(deliverable.creator.briefing_files) : [],
              endpoint: '/graphql/project/creator',
              query: PROJECT_CREATOR_SYNC_BRIEFINGFILES_MUTATION,
              key: 'upload_ids',
              // Only deliverable-specific files should be editable/removable
              value: deliverable.creator?.briefing_files
                ? ProjectsHelpers.formatBriefingFiles(deliverable.creator?.briefing_files).filter((f) => Number(f?.deliverable_id) === Number(deliverable.id))
                : [],
              value_type: 'array',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_creator_id: Number(deliverable.creator.id),
                deliverable_id: Number(deliverable.id),
              },
            },
            savings: {
              display_value:
                deliverable.creator?.initial_quote && deliverable.creator?.contract?.contract_value ? deliverable.creator.initial_quote - deliverable.creator.contract.contract_value : '',
            },
            confirmed: conceptApprovedField,
            products: {
              display_value: deliverable?.products ? ProjectsHelpers.formatProducts(deliverable.products) : [],
              endpoint: '/graphql/project/deliverable',
              query: SYNC_PROJECT_DELIVERABLE_PRODUCTS_MUTATION,
              key: 'product_ids',
              value: deliverable?.products ? ProjectsHelpers.formatProducts(deliverable.products) : [],
              value_type: 'array',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            instagram_engagement_rate: {
              display_value: deliverable.creator?.instagram_engagement_rate,
            },
            initial_quote: {
              display_value: deliverable.creator?.initial_quote ?? '',
            },
            target_live_date: {
              display_value: deliverable.target_live_date ?? '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'target_live_date',
              value_type: 'date',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            due_date: {
              display_value: deliverable.due_date ?? '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'due_date',
              value_type: 'date',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            // Keeping this for now, until its requested again lol.
            // draft_due_date: {
            //   display_value: deliverable.draft_due_date ?? '',
            //   endpoint: '/graphql/project/deliverable',
            //   query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
            //   key: 'draft_due_date',
            //   value_type: 'date',
            //   args: {
            //     project_id: Number(useProjectStore().currentProjectID),
            //     project_deliverable_id: Number(deliverable.id),
            //   },
            // },
            notes: {
              display_value: deliverable.notes,
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'notes',
              value_type: 'string',
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            destinations: {
              display_value: deliverable.destinations && deliverable.destinations.length > 0 ? deliverable.destinations : [],
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_MUTATION,
              key: 'destinations',
              value_type: 'array',
              // originalValue: deliverable.destinations && deliverable.destinations.length > 0 ? deliverable.destinations : [],
              options: deliverable.creator?.social_channels ?? [],
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            asset_version_status: {
              display_value: deliverable.destinations && deliverable.destinations[0]?.status ? deliverable.destinations[0].status : '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_ASSET_STATUS_MUTATION,
              key: 'asset_version_status',
              value_type: 'string',
              options: ProjectsHelpers.getDeliverableDestinationStatusOptions(),
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            asset_version_number: {
              display_value: deliverable.current_asset_version_number,
            },
            status: {
              display_value: deliverable.status ?? '',
              endpoint: '/graphql/project/deliverable',
              query: UPDATE_PROJECT_DELIVERABLE_STATUS_MUTATION,
              key: 'status',
              value_type: 'string',
              options: ProjectsHelpers.getDeliverableStatusOptions(),
              args: {
                project_id: Number(useProjectStore().currentProjectID),
                project_deliverable_id: Number(deliverable.id),
              },
            },
            contract_status: {
              display_value: deliverable.creator?.contract?.status ?? '',
            },
            assets: {
              // display_value: deliverable?.assets
              //   .filter((asset) => asset?.versions.length > 0)
              //   .map((asset) => asset.versions)
              //   .flat()

              display_value:
                deliverable?.assets
                  .filter((asset) => asset?.current_version)
                  .map((asset) => ({
                    ...asset.current_version,
                    project_deliverable_asset_id: asset.id,
                    isLoading: false,
                  })) ?? [],
            },
            current_asset_version: {
              //display_value: deliverable?.assets[0]?.current_version ?? ''
              display_value: deliverable?.current_asset_version ?? '',
            },
            current_asset_version_uploaded_at: {
              display_value: deliverable?.current_asset_version_uploaded_at ?? '',
            },
            ...customPropertiesFields,
            ...customEntityMappingsFields,
          };
        });
      } else {
        return [];
      }
    },
  },
});
